/*
//-------------------------------------------------------
//   Copyright (c) 2019 Templa Computer Systems Ltd
//
//   App:        EmployeePortal.UI
//   Object:     configureStore
//   Details:    Store configuration
//
// Log      Date   By  Scan Description
// -----  -------- --- ---- ------------------------------
// 102715 16/08/19 JC  M01  Added allContacts
// 103782 10/12/19 JC  M02  Expanded for training
// 103839 06/04/20 JC  M03  React upgrade
// 106380 30/04/20 JC  M04  Added PortalRedirect, Flag logout on authentication
// WI3713 24/01/24 JC  M05JC	Added DataForm, RedirectController
//-------------------------------------------------------
*/

import { applyMiddleware, combineReducers, compose, createStore } from 'redux';
import thunk from 'redux-thunk';
//M03 import { routerReducer, routerMiddleware } from 'react-router-redux';
import { connectRouter, routerMiddleware } from 'connected-react-router'; //M03
import * as ClientConfiguration from './ClientConfiguration';
import * as Authentication from './Authentication';
import * as PasswordManager from './PasswordManager';
import * as MyDetails from './MyDetails';
import * as MyPlan from './MyPlan';
import * as Weeks from './Weeks';
import * as Request from './Request';
import * as SystemControl from './SystemControl';
import * as Items from './Items';
import * as ItemTypes from './ItemTypes';
import * as Alerts from './Alerts';
import * as PrintView from './PrintView';
import * as PortalRedirect from './PortalRedirect'; //M04
import * as DataForm from './DataForm'; //M05JC
import * as RedirectController from './RedirectController'; //M05JC
import { itemCategories } from '../constants/ItemCategories';
import { lifetimeActions } from './Lifetime';

export default function configureStore(history, initialState) {
	const reducers = {
		clientConfiguration: ClientConfiguration.reducer,
		authentication: Authentication.reducer,
		passwordManager: PasswordManager.reducer,
		myDetails: MyDetails.reducer,
		myPlan: MyPlan.reducer,
		weeks: Weeks.reducer,
		request: Request.reducer,
		systemControl: SystemControl.reducer,
		alerts: Alerts.reducer,
        printView: PrintView.reducer,
		portalRedirect: PortalRedirect.reducer, //M04
		dataForm: DataForm.reducer, //M05JC
		redirectController: RedirectController.reducer, //M05JC

		unreadNotices: Items.createNamedReducer(itemCategories.unreadNotices),
		readNotices: Items.createNamedReducer(itemCategories.readNotices),
		unreadDocuments: Items.createNamedReducer(itemCategories.unreadDocuments),
		readDocuments: Items.createNamedReducer(itemCategories.readDocuments),
		unreadPay: Items.createNamedReducer(itemCategories.unreadPay),
		readPay: Items.createNamedReducer(itemCategories.readPay),
		unreadRequests: Items.createNamedReducer(itemCategories.unreadRequests),
        readRequests: Items.createNamedReducer(itemCategories.readRequests),
        allContacs: Items.createNamedReducer(itemCategories.allContacts), //M01
        uncompletedTraining: Items.createNamedReducer(itemCategories.uncompletedTraining), //M02
        completedTraining: Items.createNamedReducer(itemCategories.completedTraining), //M02

		unreadNoticeTypes: ItemTypes.createNamedReducer(itemCategories.unreadNotices),
		readNoticeTypes: ItemTypes.createNamedReducer(itemCategories.readNotices),
		unreadDocumentTypes: ItemTypes.createNamedReducer(itemCategories.unreadDocuments),
		readDocumentTypes: ItemTypes.createNamedReducer(itemCategories.readDocuments),
		unreadPayTypes: ItemTypes.createNamedReducer(itemCategories.unreadPay),
		readPayTypes: ItemTypes.createNamedReducer(itemCategories.readPay),
		unreadRequestTypes: ItemTypes.createNamedReducer(itemCategories.unreadRequests),
        readRequestTypes: ItemTypes.createNamedReducer(itemCategories.readRequests),
        allContactTypes: ItemTypes.createNamedReducer(itemCategories.allContacts), //M01
        uncompletedTrainingTypes: ItemTypes.createNamedReducer(itemCategories.uncompletedTraining),
        completedTrainingTypes: ItemTypes.createNamedReducer(itemCategories.completedTraining),
	};

	const middleware = [
		thunk,
		routerMiddleware(history)
	];

	// In development, use the browser's Redux dev tools extension if installed
	const enhancers = [];
    const isDevelopment = process.env.NODE_ENV === 'development';
    //M03 re-written below
	//if (isDevelopment && typeof window !== 'undefined' && window.devToolsExtension) {
	//	enhancers.push(window.devToolsExtension());
 //   }
    if (isDevelopment && typeof window !== 'undefined' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) {
        enhancers.push(window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__(
        ));
    }

    const appReducer = combineReducers({
        ...reducers,
        //M03 routing: routerReducer
        router: connectRouter(history) //M03
    });

	const rootReducer = (state, action) => {
		// Root reducer watches for the LOGOUT_SUCESS action to reset whole application state.
		// Therefore the LOGOUT_SUCESS action is not required to be handled by the authentication reducer.
		// Yes, reducers shouldn't have a side effect, but I'll make an exception as we're tearing down the app...
		if (action.type === Authentication.logoutSuccessType) {
			const { clientConfiguration } = state;
            state = { ...initialState, clientConfiguration: clientConfiguration, authentication: { message: action.message, loggedOut: true } }; //M04 flag as logged out
			lifetimeActions.onLogOut();
		}
		// Debugging - show actions and resulting states as they are fired
		// NOTE THAT THE STATE WILL BE AS BEFORE THE ACTION IS DISPATCHED!!
		// console.log({ type: action.type, action, state });
		return appReducer(state, action);
	};

	return createStore(
		rootReducer,
		initialState,
		compose(applyMiddleware(...middleware), ...enhancers)
	);
}